import OnCloseSaveWarningModal from 'modules/app/modals/OnCloseWarningModal';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from 'ui';
import YBugFloatingButton from 'modules/app/yBugFloatingButton';
import { modals } from './modals';
import { IModalOptions } from './types';

const defaultOptions: IModalOptions = {
  variant: 'fullRight',
  contentLabel: '',
  name: '',
  context: {},
  onClose: () => {},
};

interface IModalContext {
  /** Current modal state. */
  options: IModalOptions;
  setOptions: React.Dispatch<React.SetStateAction<IModalOptions>>;

  /** Open a modal with given configuration. */
  open: (options: IModalOptions) => void;

  /** Close the current modal. */
  close: (
    event?: SyntheticEvent | null,
    /** Provides option to force show save warning at caller level. */
    showWarning?: boolean
  ) => void;
  isOpen: boolean;
}

const ModalContext = createContext<IModalContext>({
  options: defaultOptions,
  setOptions: () => null,
  open: (option) => null,
  close: () => null,
  isOpen: false,
});

const NulComponent: FC = () => null;

const ModalProvider = ({ children }: PropsWithChildren) => {
  const [options, setOptions] = useState<IModalOptions>(defaultOptions);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const pendingOpenRef = useRef<IModalOptions | null>(null);

  const {
    variant,
    name,
    context,
    contentLabel,
    modal,
    showWarningBeforeClose = false,
    warningText,
    onClose,
  } = options;

  const handleWarningConfirmation = () => {
    setShowWarning(false);
    setOptions(defaultOptions);
  };

  const close = (event?: SyntheticEvent | null, _showWarning = false) => {
    if (showWarningBeforeClose || _showWarning) {
      setShowWarning(true);
    } else {
      setOptions(defaultOptions);
      if (onClose) onClose();
    }
  };

  const open = (option: IModalOptions) => {
    // If a modal is currently open, queue the next one and close first.
    // The queued modal is opened explicitly in an effect when close has completed.
    if (options.modal) {
      pendingOpenRef.current = option;
      setOptions(defaultOptions);
      return;
    }

    setOptions(option);
  };

  const ModalComponent = modal ? modals[modal] : NulComponent;
  const isOpen = !!modal;

  useEffect(() => {
    if (options.modal || !pendingOpenRef.current) return;

    const queuedModalOptions = pendingOpenRef.current;
    pendingOpenRef.current = null;
    setOptions(queuedModalOptions);
  }, [options.modal]);

  const store = useMemo(
    () => ({
      options,
      setOptions,
      close,
      open,
      isOpen,
    }),
    [modal]
  );

  /**
   * Render warning modal on close if set in modal options.
   */
  const renderWarningModal = () => (
    <Modal
      isOpen={showWarning}
      onRequestClose={() => setShowWarning(false)}
      variant="center"
      contentLabel=""
      name="Warning"
    >
      <OnCloseSaveWarningModal
        text={warningText}
        onAbort={() => setShowWarning(false)}
        onConfirm={handleWarningConfirmation}
      />
    </Modal>
  );

  return (
    <ModalContext.Provider value={store}>
      {children}
      {renderWarningModal()}
      {/* Render yBug Floating Button when Modal Window is visible with conditional position */}
      {isOpen && <YBugFloatingButton position={variant === 'fullRight' ? 'left' : 'right'} />}
      <Modal
        isOpen={isOpen}
        onRequestClose={close}
        variant={variant}
        contentLabel={contentLabel}
        name={name}
        id={options?.id}
      >
        {/* Modal map has heterogeneous context props; keep loose cast here */}
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {isOpen && <ModalComponent {...(context as any)} />}
      </Modal>
    </ModalContext.Provider>
  );
};

export const useModal = () => useContext(ModalContext);

export default ModalProvider;
